import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GripVertical, Edit3, Trash2, Plus, Lock, Check, X, Loader2, ArrowRightLeft } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import EhiDocCleanup from "./EhiDocCleanup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ehi-doc-categories`;
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return (typeof d === "string" ? d : d?.message) || fb; };
const INPUT = { fontSize: 13, padding: "7px 10px", border: "1px solid var(--border, #e5e5e5)", borderRadius: 6, outline: "none", background: "#fff" };
const ICON = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, border: 0, background: "transparent", cursor: "pointer", color: "#555" };

function Row({ cat, onSave, onDelete, onMoveAll }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(cat.name);
  const [desc, setDesc] = useState(cat.description || "");
  const save = async () => { if (await onSave(cat, { name: cat.is_system ? undefined : name, description: desc })) setEdit(false); };
  return (
    <div ref={setNodeRef} data-testid={`doc-cat-row-${cat.id}`} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1,
      display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderBottom: "0.5px solid #eee", background: isDragging ? "#F5F9FF" : "#fff" }}>
      <button {...attributes} {...listeners} data-testid={`doc-cat-drag-${cat.id}`} title="Drag to reorder" style={{ ...ICON, cursor: "grab", color: "#999" }}><GripVertical style={{ width: 14, height: 14 }} /></button>
      {edit ? (
        <div style={{ flex: 1, display: "flex", gap: 8 }}>
          <input data-testid={`doc-cat-edit-name-${cat.id}`} value={name} disabled={cat.is_system} onChange={(e) => setName(e.target.value)} style={{ ...INPUT, width: 200 }} />
          <input data-testid={`doc-cat-edit-desc-${cat.id}`} value={desc} placeholder="Description (optional)" onChange={(e) => setDesc(e.target.value)} style={{ ...INPUT, flex: 1 }} />
        </div>
      ) : (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
            {cat.name}{cat.is_system && <Lock style={{ width: 11, height: 11, color: "#999" }} aria-label="Permanent" />}
          </div>
          {cat.description && <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>{cat.description}</div>}
        </div>
      )}
      <span data-testid={`doc-cat-count-${cat.id}`} style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#F3F4F6", color: "#4B5563", whiteSpace: "nowrap" }}>{cat.doc_count} doc{cat.doc_count === 1 ? "" : "s"}</span>
      {edit ? (<>
        <button data-testid={`doc-cat-save-${cat.id}`} onClick={save} title="Save" style={{ ...ICON, color: "#1D9E75" }}><Check style={{ width: 14, height: 14 }} /></button>
        <button data-testid={`doc-cat-cancel-${cat.id}`} onClick={() => { setEdit(false); setName(cat.name); setDesc(cat.description || ""); }} title="Cancel" style={ICON}><X style={{ width: 14, height: 14 }} /></button>
      </>) : (<>
        <button data-testid={`doc-cat-edit-${cat.id}`} onClick={() => setEdit(true)} title="Edit" style={ICON}><Edit3 style={{ width: 13, height: 13 }} /></button>
        <button data-testid={`doc-cat-moveall-${cat.id}`} onClick={() => onMoveAll(cat)} disabled={!cat.doc_count} title={cat.doc_count ? "Move all documents to another category" : "No documents to move"}
          style={{ ...ICON, opacity: cat.doc_count ? 1 : 0.3, cursor: cat.doc_count ? "pointer" : "not-allowed" }}><ArrowRightLeft style={{ width: 13, height: 13 }} /></button>
        <button data-testid={`doc-cat-delete-${cat.id}`} onClick={() => onDelete(cat)} disabled={cat.is_system} title={cat.is_system ? "Uncategorized can't be deleted" : "Delete"}
          style={{ ...ICON, color: "#B42318", opacity: cat.is_system ? 0.3 : 1, cursor: cat.is_system ? "not-allowed" : "pointer" }}><Trash2 style={{ width: 13, height: 13 }} /></button>
      </>)}
    </div>
  );
}

function MoveDialog({ cat, cats, onClose, onConfirm, mode = "delete" }) {
  const options = cats.filter((c) => c.id !== cat.id);
  const [target, setTarget] = useState(options.some((c) => c.id === "uncategorized") ? "uncategorized" : options[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const del = mode === "delete";
  const n = cat.doc_count;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={del ? "doc-cat-move-dialog" : "doc-cat-moveall-dialog"}>
        <DialogHeader>
          <DialogTitle>{del ? `Delete "${cat.name}"` : `Move all documents from "${cat.name}"`}</DialogTitle>
          <DialogDescription data-testid="doc-cat-move-count">
            {n} document{n === 1 ? " uses" : "s use"} this category. {del ? `Pick where to move ${n === 1 ? "it" : "them"} before deleting.` : `Pick where to move ${n === 1 ? "it" : "them"}. The category itself stays.`}
          </DialogDescription>
        </DialogHeader>
        <select data-testid="doc-cat-move-target" value={target} onChange={(e) => setTarget(e.target.value)} style={{ ...INPUT, width: "100%" }}>
          {options.map((c) => <option key={c.id} value={c.id} title={c.description || ""}>{c.name}</option>)}
        </select>
        <DialogFooter>
          <button data-testid="doc-cat-move-cancel" className="btn" onClick={onClose}>Cancel</button>
          <button data-testid="doc-cat-move-confirm" className="btn btn-primary" disabled={busy || !target} style={del ? { background: "#B42318", borderColor: "#B42318" } : undefined}
            onClick={async () => { setBusy(true); await onConfirm(target); setBusy(false); }}>
            {busy ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : del ? "Move documents & delete" : `Move ${n} document${n === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EhiDocCategoriesPanel({ token }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [moving, setMoving] = useState(null);
  const [movingAll, setMovingAll] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const load = useCallback(() => {
    axios.get(API, h).then((r) => setCats(r.data.categories || [])).catch((e) => toast.error(errMsg(e, "Couldn't load categories"))).finally(() => setLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error("Category name is required"); return; }
    try { await axios.post(API, { name, description: desc }, h); setName(""); setDesc(""); toast.success("Category added"); load(); }
    catch (e) { toast.error(errMsg(e, "Couldn't add category")); }
  };
  const save = async (cat, body) => {
    if (body.name !== undefined && !body.name.trim()) { toast.error("Category name is required"); return false; }
    try { await axios.patch(`${API}/${cat.id}`, body, h); toast.success("Category updated"); load(); return true; }
    catch (e) { toast.error(errMsg(e, "Couldn't update category")); return false; }
  };
  const del = async (cat, moveTo) => {
    try {
      const r = await axios.delete(`${API}/${cat.id}`, { ...h, params: moveTo ? { move_to: moveTo } : {} });
      toast.success(r.data.moved ? `Moved ${r.data.moved} document${r.data.moved === 1 ? "" : "s"} and deleted "${cat.name}"` : `Deleted "${cat.name}"`);
      setMoving(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.doc_count) { setMoving({ ...cat, doc_count: d.doc_count }); load(); return; }
      toast.error(errMsg(e, "Couldn't delete category"));
    }
  };
  const onDelete = (cat) => {
    if (cat.doc_count > 0) { setMoving(cat); return; }
    if (window.confirm(`Delete "${cat.name}"?`)) del(cat);
  };
  const moveAll = async (cat, to) => {
    try {
      const r = await axios.post(`${API}/move`, { from_category_id: cat.id, to_category_id: to }, h);
      toast.success(`Moved ${r.data.moved} document${r.data.moved === 1 ? "" : "s"} to "${cats.find((c) => c.id === to)?.name}"`);
      setMovingAll(null); load();
    } catch (e) { toast.error(errMsg(e, "Couldn't move the documents")); }
  };
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(cats, cats.findIndex((c) => c.id === active.id), cats.findIndex((c) => c.id === over.id));
    setCats(next);
    try { await axios.put(`${API}/order`, { ids: next.map((c) => c.id) }, h); }
    catch (e) { toast.error(errMsg(e, "Couldn't save the order")); load(); }
  };

  return (
    <div data-testid="ehi-doc-categories-panel">
      <div className="card-title" style={{ marginBottom: 4 }}>EHI Document Categories</div>
      <div style={{ fontSize: 12, color: "#777", marginBottom: 14 }}>One shared list for every practice's Documents tab. Drag to set the order used in the upload and filter dropdowns.</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input data-testid="doc-cat-new-name" placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} style={{ ...INPUT, width: 220 }} />
        <input data-testid="doc-cat-new-desc" placeholder="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} style={{ ...INPUT, flex: 1 }} />
        <button data-testid="doc-cat-add" className="btn btn-primary" onClick={add} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Plus style={{ width: 13, height: 13 }} /> Add</button>
      </div>
      <div style={{ border: "0.5px solid #eee", borderRadius: 8, overflow: "hidden" }}>
        {loading ? <div style={{ padding: 20, fontSize: 12, color: "#888" }}>Loading…</div> : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={cats.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              {cats.map((c) => <Row key={`${c.id}-${c.name}-${c.description}`} cat={c} onSave={save} onDelete={onDelete} onMoveAll={setMovingAll} />)}
            </SortableContext>
          </DndContext>
        )}
      </div>
      {moving && <MoveDialog cat={moving} cats={cats} onClose={() => setMoving(null)} onConfirm={(t) => del(moving, t)} />}
      {movingAll && <MoveDialog mode="move" cat={movingAll} cats={cats} onClose={() => setMovingAll(null)} onConfirm={(t) => moveAll(movingAll, t)} />}
      {!loading && <EhiDocCleanup token={token} cats={cats} reloadKey={cats} onMoved={load} />}
    </div>
  );
}
